/**
 * ClashMiniGames - Cribbage Clash Interactive Mini-Games & Billboard System
 * Encapsulates Sign Pickers, 3-Die Roll with physics and target selection,
 * Spin-The-Wheel canvas animation, and track billboard text synchronization.
 */

import { ClashManager } from './ClashManager.js';

export const CLASH_SIGN_DEFS = Object.freeze({
  'Plus 5': { icon: '➕5️⃣', desc: 'Move ahead 5 points' },
  'Plus 10': { icon: '⏩🔟', desc: 'Move ahead 10 points' },
  'Minus 5': { icon: '➖5️⃣', desc: 'Move back 5 points' },
  'Minus 10': { icon: '⏪🔟', desc: 'Move back 10 points' },
  'Undo': { icon: '↩️', desc: 'Undo your last points scored' },
  'Go To Jail': { icon: '🔒', desc: 'Sent to the nearest Jail block' },
  'Go To The Bar': { icon: '🍺', desc: 'Sent to the nearest Bar block' },
  'Go To Timeout': { icon: '⏳', desc: 'Sent to the nearest Timeout block' },
  'Score Plus x2': { icon: '✖️2️⃣', desc: 'Move ahead 2x last points' },
  'Score Plus x3': { icon: '🚀3️⃣', desc: 'Move ahead 3x last points' },
  'Score Minus x2': { icon: '📉2️⃣', desc: 'Move back 2x last points' },
  'Score Minus x3': { icon: '💥3️⃣', desc: 'Move back 3x last points' },
  'Roll The Dice': { icon: '🎲', desc: 'Roll 3 dice for Direction, Pts & Target' },
  'Spin The Wheel': { icon: '🎡', desc: 'Spin the wheel with 8 prize/penalty slots' }
});

export const CLASH_WHEEL_SLOTS = Object.freeze([
  'Plus 5',
  'Minus 5',
  'Plus 10',
  'Minus 10',
  'Go To Jail',
  'Go To The Bar',
  'Go To Timeout',
  'Roll The Dice'
]);

export class ClashMiniGames {
  static get SIGN_DEFS() {
    return CLASH_SIGN_DEFS;
  }

  static get WHEEL_SLOTS() {
    return CLASH_WHEEL_SLOTS;
  }

  /**
   * Helper to resolve runtime environment context.
   * @private
   */
  static _resolveCtx(ctx = {}) {
    const doc = ctx.document || (typeof document !== 'undefined' ? document : null);
    const win = ctx.window || (typeof window !== 'undefined' ? window : null);
    const st = ctx.state || (win && win.state) || {};
    const set = ctx.settings || (win && win.settings) || {};
    const logFn = ctx.log || (win && typeof win.log === 'function' ? win.log : () => {});
    const fancyFn = ctx.fancyCall || (win && typeof win.fancyCall === 'function' ? win.fancyCall : () => {});
    const toastFn = ctx.showScoreToast || (win && typeof win.showScoreToast === 'function' ? win.showScoreToast : () => {});
    const statusFn = ctx.setStatus || (win && typeof win.setStatus === 'function' ? win.setStatus : () => {});
    const modalFn = ctx.showModal || (win && typeof win.showModal === 'function' ? win.showModal : () => {});
    const closeFn = ctx.closeModal || (win && typeof win.closeModal === 'function' ? win.closeModal : () => {});
    const pNameFn = ctx.pName || (win && typeof win.pName === 'function' ? win.pName : (w => w));
    const isHumanFn = ctx.isSeatHuman || (win && typeof win.isSeatHuman === 'function' ? win.isSeatHuman : (() => true));
    const isCpuFn = ctx.isSeatCpu || (win && typeof win.isSeatCpu === 'function' ? win.isSeatCpu : (() => false));
    const scoreKeyFn = ctx.scoreKey || (win && typeof win.scoreKey === 'function' ? win.scoreKey : (w => w));
    const addScoreFn = ctx.addScore || (win && typeof win.addScore === 'function' ? win.addScore : () => {});
    const renderScoresFn = ctx.renderScores || (win && typeof win.renderScores === 'function' ? win.renderScores : () => {});
    const sfxObj = ctx.sfx || (win && win.sfx) || { card: () => {}, pop: () => {}, peg: () => {} };

    return {
      doc, win, state: st, settings: set, log: logFn, fancyCall: fancyFn,
      showScoreToast: toastFn, setStatus: statusFn, showModal: modalFn,
      closeModal: closeFn, pName: pNameFn, isSeatHuman: isHumanFn,
      isSeatCpu: isCpuFn, scoreKey: scoreKeyFn, addScore: addScoreFn,
      renderScores: renderScoresFn, sfx: sfxObj
    };
  }

  /**
   * Handles player landing on an interactive Signs block.
   * @param {Object} block
   * @param {string} who
   * @param {Object} [context={}]
   */
  static handleSignsLanding(block, who, context = {}) {
    const ctx = ClashMiniGames._resolveCtx(context);
    if (!ctx.state || !ctx.settings.optSigns || !block) return;

    if (!ctx.state.signsState) ctx.state.signsState = {};
    if (!ctx.state.signsState[block.id] || !ctx.state.signsState[block.id].length) {
      ctx.state.signsState[block.id] = [...(block.initialSigns || [])];
    }
    const remaining = ctx.state.signsState[block.id];
    const isHuman = ctx.isSeatHuman(who);

    if (isHuman) {
      ctx.state.pausedForSign = true;
      ctx.state.paused = true;
      const playerName = ctx.pName(who);
      ctx.log(`🪧 SIGNS: ${playerName} landed on Sign Block ${block.id} (Holes ${block.start}–${block.end})! Game is paused until you pick a sign.`);
      ctx.setStatus(`Landed on Sign Block ${block.id}! Game is paused — pick a sign.`);
      ClashMiniGames.openSignPickerModal(block, who, context);
    } else {
      const chosen = ClashManager.pickCpuSign(remaining);
      const cpuName = ctx.pName(who);
      ctx.log(`🪧 SIGNS: ${cpuName} landed on Sign Block ${block.id} and selected "${chosen}"!`);
      ctx.fancyCall(`${cpuName} SELECTED: ${chosen.toUpperCase()}!`, 'signs', 3400);
      ctx.showScoreToast(who, 0, `${cpuName} selected: ${chosen}`);
      ctx.setStatus(`${cpuName} landed on Sign Block ${block.id} and selected: ${chosen}`);

      setTimeout(() => {
        ClashMiniGames.chooseSign(chosen, who, block, context);
      }, 2200);
    }
  }

  /**
   * Opens the interactive sign card picker modal.
   * @param {Object} block
   * @param {string} who
   * @param {Object} [context={}]
   */
  static openSignPickerModal(block, who, context = {}) {
    const ctx = ClashMiniGames._resolveCtx(context);
    if (!ctx.state || !block) return;

    ctx.state._inSignAction = true;
    ctx.state.pausedForSign = true;
    ctx.state.paused = true;

    const remaining = (ctx.state.signsState && ctx.state.signsState[block.id]) || [...(block.initialSigns || [])];
    const cardsHtml = remaining.map(sign => {
      const def = CLASH_SIGN_DEFS[sign] || { icon: '🪧', desc: sign };
      const escapedSign = sign.replace(/'/g, "\\'");
      return `
        <div class="sign-pick-card" data-action="pick-sign" data-sign="${sign}" data-block="${block.id}" data-who="${who}">
          <div class="sign-card-icon">${def.icon}</div>
          <div class="sign-card-name">${sign}</div>
          <div class="sign-card-desc">${def.desc}</div>
        </div>
      `;
    }).join('');

    const modalHtml = `
      <div class="signs-modal-wrap">
        <h2 class="signs-modal-title">🪧 SIGN BLOCK ${block.id}</h2>
        <p class="signs-modal-subtitle">Landed on Holes ${block.start}–${block.end}! Game is paused — choose an available sign (${remaining.length} left):</p>
        <div class="signs-cards-grid">
          ${cardsHtml}
        </div>
      </div>
    `;
    ctx.showModal(modalHtml, false);
    if (typeof document !== 'undefined') {
      const wrap = document.querySelector('.signs-modal-wrap');
      if (wrap) {
        wrap.addEventListener('click', (e) => {
          const card = e.target.closest('[data-action="pick-sign"]');
          if (card) {
            const sName = card.dataset.sign;
            const bId = parseInt(card.dataset.block, 10);
            const w = card.dataset.who;
            if (typeof onSignCardClick === 'function') {
              onSignCardClick(sName, bId, w);
            } else {
              ClashMiniGames.onSignCardClick(sName, bId, w, context);
            }
          }
        });
      }
    }
  }

  /**
   * Handles user click on a sign card in the picker modal.
   * @param {string} signName
   * @param {number} blockId
   * @param {string} who
   * @param {Object} [context={}]
   */
  static onSignCardClick(signName, blockId, who, context = {}) {
    const ctx = ClashMiniGames._resolveCtx(context);
    ctx.closeModal();

    const getSignsFn = context.getSignConfigs || (ctx.win && ctx.win.getSignConfigs);
    const blocks = (typeof getSignsFn === 'function') ? getSignsFn() : [];
    const block = blocks.find(b => b.id === blockId);
    if (block) {
      ClashMiniGames.chooseSign(signName, who, block, context);
    }
  }

  /**
   * Selects and consumes a sign card from a block.
   * @param {string} signName
   * @param {string} who
   * @param {Object} block
   * @param {Object} [context={}]
   */
  static chooseSign(signName, who, block, context = {}) {
    const ctx = ClashMiniGames._resolveCtx(context);
    if (!ctx.state || !block) return;

    ctx.state._inSignAction = true;
    if (!ctx.state.signsState) ctx.state.signsState = {};
    if (!ctx.state.signsState[block.id]) ctx.state.signsState[block.id] = [...(block.initialSigns || [])];

    const idx = ctx.state.signsState[block.id].indexOf(signName);
    if (idx !== -1) {
      ctx.state.signsState[block.id].splice(idx, 1);
    }

    const isHuman = ctx.isSeatHuman(who);
    const playerName = ctx.pName(who);

    if (!isHuman) {
      ctx.log(`🪧 SIGN CHOSEN: ${playerName} selected "${signName}" on Block ${block.id}!`);
      ctx.fancyCall(`${playerName} SELECTED: ${signName.toUpperCase()}!`, 'signs', 3400);
      ctx.showScoreToast(who, 0, `${playerName} chose ${signName}`);
      ctx.setStatus(`${playerName} selected Sign: ${signName}`);
    } else {
      ctx.log(`🪧 SIGN CHOSEN: ${playerName} selected "${signName}" on Block ${block.id}!`);
      ctx.fancyCall(signName.toUpperCase(), 'signs', 2600);
      ctx.showScoreToast(who, 0, `Chose Sign: ${signName}`);
    }

    const wasLast = ctx.state.signsState[block.id].length === 0;
    if (wasLast) {
      ctx.state.signsState[block.id] = [...(block.initialSigns || [])];
      ctx.log(`✨ SIGNS REPLENISHED: The last sign was chosen on Block ${block.id}! All ${block.initialSigns.length} signs have been restored.`);
      setTimeout(() => {
        ctx.fancyCall('SIGNS REPLENISHED!', 'signs', 2600);
      }, 1500);
    }

    ClashMiniGames.updateSignsBillboard(block.id, context);

    setTimeout(() => {
      ClashMiniGames.executeSign(signName, who, block, context);
      ctx.state._inSignAction = false;
      if (signName !== 'Roll The Dice' && signName !== 'Spin The Wheel') {
        ClashMiniGames.resumeGameAfterSign(context);
      }
    }, isHuman ? 1400 : 1800);
  }

  /**
   * Resumes the match progression after a sign effect completes.
   * @param {Object} [context={}]
   */
  static resumeGameAfterSign(context = {}) {
    const ctx = ClashMiniGames._resolveCtx(context);
    if (!ctx.state) return;

    ctx.state.pausedForSign = false;
    if (!ctx.state.pausedForAdultTimer && !ctx.state.pausedForAdultStrip && !ctx.state.pausedForPintsBonus) {
      ctx.state.paused = false;
    }

    if (typeof ctx.state._resumeAfterSign === 'function' && !ctx.state.pausedForAdultTimer && !ctx.state.pausedForAdultStrip && !ctx.state.pausedForPintsBonus) {
      const resumeFn = ctx.state._resumeAfterSign;
      ctx.state._resumeAfterSign = null;
      ctx.state._resumeAfterAdultTimer = null;
      ctx.state._resumeAfterPintsBonus = null;
      resumeFn();
    } else if (ctx.state.phase === 'play' && ctx.isSeatCpu(ctx.state.turn) && !ctx.state.paused) {
      const schedFn = context.scheduleOppPlay || (ctx.win && ctx.win.scheduleOppPlay);
      if (typeof schedFn === 'function') {
        schedFn(ctx.state.turn);
      }
    }
  }

  /**
   * Executes the mechanical effect of a chosen sign.
   * @param {string} signName
   * @param {string} who
   * @param {Object|null} block
   * @param {Object} [context={}]
   */
  static executeSign(signName, who, block = null, context = {}) {
    const ctx = ClashMiniGames._resolveCtx(context);
    const key = ctx.scoreKey(who);
    const curScore = (ctx.state.scores && ctx.state.scores[key]) || 0;
    const lastDelta = Math.abs((ctx.state.lastScoreDelta && ctx.state.lastScoreDelta[key]) || 2);
    const playerName = ctx.pName(who);

    switch (signName) {
      case 'Plus 5':
        ctx.addScore(who, 5, 'Sign: Plus 5', false, true);
        break;
      case 'Plus 10':
        ctx.addScore(who, 10, 'Sign: Plus 10', false, true);
        break;
      case 'Minus 5':
        ctx.addScore(who, -5, 'Sign: Minus 5', false, true);
        break;
      case 'Minus 10':
        ctx.addScore(who, -10, 'Sign: Minus 10', false, true);
        break;
      case 'Undo':
        ctx.log(`↩️ UNDO SIGN: Reversing last points (${lastDelta} pts) for ${playerName}.`);
        ctx.addScore(who, -lastDelta, 'Sign: Undo', false, true);
        break;
      case 'Score Plus x2':
        ctx.addScore(who, lastDelta * 2, 'Sign: Score Plus x2', false, true);
        break;
      case 'Score Plus x3':
        ctx.addScore(who, lastDelta * 3, 'Sign: Score Plus x3', false, true);
        break;
      case 'Score Minus x2':
        ctx.addScore(who, -(lastDelta * 2), 'Sign: Score Minus x2', false, true);
        break;
      case 'Score Minus x3':
        ctx.addScore(who, -(lastDelta * 3), 'Sign: Score Minus x3', false, true);
        break;
      case 'Go To Jail': {
        const getJailsFn = context.getJailConfigs || (ctx.win && ctx.win.getJailConfigs);
        const jails = (typeof getJailsFn === 'function' && ctx.settings.optJail) ? getJailsFn() : [];
        if (jails.length) {
          const nearest = jails.slice().sort((a, b) => Math.abs(a.hole - curScore) - Math.abs(b.hole - curScore))[0];
          ctx.state.scores[key] = nearest.hole;
          ctx.state.jailed = ctx.state.jailed || {};
          ctx.state.jailed[key] = { jailHole: nearest.hole, turnsRemaining: 3, step: 0 };
          ctx.log(`🔒 GO TO JAIL: ${playerName} sent to nearest Jail at Hole ${nearest.hole}!`);
          ctx.fancyCall('SENT TO JAIL!', 'jail', 2400);
          ctx.renderScores(true);

          const jailOpt = (ctx.settings && ctx.settings.jailAnimation) || (typeof window !== 'undefined' && window.settings && window.settings.jailAnimation) || 'yes';
          if (jailOpt !== 'no' && jailOpt !== 'off' && jailOpt !== false) {
            const showJail = context.showJailAnimation || (typeof window !== 'undefined' && window.showJailAnimation) || (typeof window !== 'undefined' && window.JailAnimationOverlay && window.JailAnimationOverlay.show);
            if (typeof showJail === 'function') {
              try {
                showJail({
                  playerName,
                  who,
                  jailHole: nearest.hole,
                  score: nearest.hole,
                  settings: ctx.settings
                });
              } catch (e) {}
            }
          }
        } else {
          ctx.log(`ℹ️ GO TO JAIL: No Jail block is currently active on the board.`);
          ctx.fancyCall('NO JAIL ACTIVE', 'signs', 1600);
        }
        break;
      }
      case 'Go To The Bar': {
        const getBarsFn = context.getBarConfigs || (ctx.win && ctx.win.getBarConfigs);
        const bars = (typeof getBarsFn === 'function' && ctx.settings.optBar) ? getBarsFn() : [];
        if (bars.length) {
          const nearest = bars.slice().sort((a, b) => Math.abs(a.hole - curScore) - Math.abs(b.hole - curScore))[0];
          ctx.state.scores[key] = nearest.hole;
          ctx.state.atBar = ctx.state.atBar || {};
          ctx.state.atBar[key] = { barHole: nearest.hole, turnsRemaining: 3, step: 0 };
          ctx.log(`🍻 GO TO THE BAR: ${playerName} sent to nearest Bar at Hole ${nearest.hole}!`);
          ctx.fancyCall('SENT TO THE BAR!', 'bar', 2400);
          ctx.renderScores(true);

          const barOpt = (ctx.settings && ctx.settings.barAnimation) || (typeof window !== 'undefined' && window.settings && window.settings.barAnimation) || 'yes';
          if (barOpt !== 'no' && barOpt !== 'off' && barOpt !== false) {
            const showBar = context.showBarAnimation || (typeof window !== 'undefined' && window.showBarAnimation) || (typeof window !== 'undefined' && window.BarAnimationOverlay && window.BarAnimationOverlay.show);
            if (typeof showBar === 'function') {
              let playerCard = null;
              if (ctx.state && ctx.state.lastPlayedCard) playerCard = ctx.state.lastPlayedCard;
              else if (ctx.state && ctx.state.hands && ctx.state.hands[who] && ctx.state.hands[who].length) playerCard = ctx.state.hands[who][0];
              try {
                showBar({
                  playerName,
                  who,
                  barHole: nearest.hole,
                  score: nearest.hole,
                  card: playerCard,
                  settings: ctx.settings
                });
              } catch (e) {}
            }
          }
        } else {
          ctx.log(`ℹ️ GO TO THE BAR: No Bar block is currently active on the board.`);
          ctx.fancyCall('NO BAR ACTIVE', 'signs', 1600);
        }
        break;
      }
      case 'Go To Timeout': {
        const getTimeoutsFn = context.getTimeoutConfigs || (ctx.win && ctx.win.getTimeoutConfigs);
        const timeouts = (typeof getTimeoutsFn === 'function' && ctx.settings.optTimeout) ? getTimeoutsFn() : [];
        if (timeouts.length) {
          const nearest = timeouts.slice().sort((a, b) => Math.abs(a.hole - curScore) - Math.abs(b.hole - curScore))[0];
          ctx.state.scores[key] = nearest.hole;
          ctx.state.inTimeout = ctx.state.inTimeout || {};
          ctx.state.inTimeout[key] = { timeoutHole: nearest.hole, turnsRemaining: 3, step: 0 };
          ctx.log(`⏳ GO TO TIMEOUT: ${playerName} sent to nearest Timeout at Hole ${nearest.hole}!`);
          ctx.fancyCall('SENT TO TIMEOUT!', 'timeout', 2400);
          ctx.renderScores(true);
        } else {
          ctx.log(`ℹ️ GO TO TIMEOUT: No Timeout block is currently active on the board.`);
          ctx.fancyCall('NO TIMEOUT ACTIVE', 'signs', 1600);
        }
        break;
      }
      case 'Roll The Dice':
        ClashMiniGames.openDiceRollModal(who, context);
        break;
      case 'Spin The Wheel':
        ClashMiniGames.openWheelSpinModal(who, context);
        break;
      default:
        ctx.log(`🪧 SIGN: Unknown sign "${signName}"`);
        break;
    }
  }

  /**
   * Opens the 3-die roll arena modal.
   * @param {string} who
   * @param {Object} [context={}]
   */
  static openDiceRollModal(who, context = {}) {
    const ctx = ClashMiniGames._resolveCtx(context);
    const modalHtml = `
      <div class="signs-modal-wrap">
        <h2 class="signs-modal-title">🎲 ROLL THE DICE</h2>
        <p class="signs-modal-subtitle">Rolling 3 dice for Direction (±), Points (5..30), and Target Player:</p>
        <div class="dice-arena">
          <div class="die-block die-dir" id="die1">
            <span class="die-label">Direction</span>
            <span class="die-value" id="die1Val">?</span>
          </div>
          <div class="die-block die-val" id="die2">
            <span class="die-label">Points</span>
            <span class="die-value" id="die2Val">?</span>
          </div>
          <div class="die-block die-target" id="die3">
            <span class="die-label">Target</span>
            <span class="die-value" id="die3Val" style="font-size: 1rem;">?</span>
          </div>
        </div>
        <div id="diceActions">
          <button type="button" class="dice-roll-btn" id="btnRollDice">ROLLING DICE...</button>
        </div>
        <div id="diceTargetPick" class="target-pick-wrap" style="display: none;"></div>
      </div>
    `;
    ctx.showModal(modalHtml, false);
    const rollBtn = ctx.doc ? ctx.doc.getElementById('btnRollDice') : null;
    if (rollBtn) {
      rollBtn.addEventListener('click', () => {
        if (typeof performDiceRoll === 'function') performDiceRoll(who);
        else ClashMiniGames.performDiceRoll(who, context);
      });
    }

    setTimeout(() => {
      ClashMiniGames.performDiceRoll(who, context);
    }, 300);
  }

  /**
   * Animates and resolves the 3-die scramble roll.
   * @param {string} who
   * @param {Object} [context={}]
   */
  static performDiceRoll(who, context = {}) {
    const ctx = ClashMiniGames._resolveCtx(context);
    if (ctx.state && ctx.state._diceRolling) return;
    if (ctx.state) ctx.state._diceRolling = true;

    const doc = ctx.doc;
    const btn = doc ? doc.getElementById('btnRollDice') : null;
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'ROLLING... (2s)';
    }

    const d1 = doc ? doc.getElementById('die1') : null;
    const d2 = doc ? doc.getElementById('die2') : null;
    const d3 = doc ? doc.getElementById('die3') : null;
    const d1V = doc ? doc.getElementById('die1Val') : null;
    const d2V = doc ? doc.getElementById('die2Val') : null;
    const d3V = doc ? doc.getElementById('die3Val') : null;

    if (d1) { d1.classList.add('rolling'); d1.classList.remove('settled'); }
    if (d2) { d2.classList.add('rolling'); d2.classList.remove('settled'); }
    if (d3) { d3.classList.add('rolling'); d3.classList.remove('settled'); }

    ctx.sfx.card();

    const dirOptions = ['+', '−'];
    const valOptions = [5, 10, 15, 20, 25, 30];
    const targetOptions = ['You', 'Opponent', 'You Pick'];

    const scrambleTimer = setInterval(() => {
      if (d1V) d1V.textContent = dirOptions[Math.floor(Math.random() * dirOptions.length)];
      if (d2V) d2V.textContent = valOptions[Math.floor(Math.random() * valOptions.length)];
      if (d3V) d3V.textContent = targetOptions[Math.floor(Math.random() * targetOptions.length)];
    }, 65);

    setTimeout(() => {
      clearInterval(scrambleTimer);
      if (ctx.state) ctx.state._diceRolling = false;

      if (d1) { d1.classList.remove('rolling'); d1.classList.add('settled'); }
      if (d2) { d2.classList.remove('rolling'); d2.classList.add('settled'); }
      if (d3) { d3.classList.remove('rolling'); d3.classList.add('settled'); }

      const dir = Math.random() < 0.5 ? '+' : '−';
      const val = valOptions[Math.floor(Math.random() * valOptions.length)];
      const die3Pool = ['You', 'You', 'Opponent', 'Opponent', 'You Pick', 'You Pick'];
      const targetType = die3Pool[Math.floor(Math.random() * die3Pool.length)];

      if (d1V) d1V.textContent = dir;
      if (d2V) d2V.textContent = val;
      if (d3V) d3V.textContent = targetType;

      ctx.sfx.pop();

      const isPositive = dir === '+';
      const signedPts = isPositive ? val : -val;

      ctx.log(`🎲 DICE STOPPED: [${dir}] [${val} pts] [${targetType}]!`);

      const isCpu = ctx.isSeatCpu(who);
      const getTracksFn = context.getActiveTracks || (ctx.win && ctx.win.getActiveTracks);
      const tracks = (typeof getTracksFn === 'function') ? getTracksFn() : ['p1', 'p2'];
      const opponents = tracks.filter(t => t !== who);
      const actContainer = doc ? doc.getElementById('diceActions') : null;
      const pickContainer = doc ? doc.getElementById('diceTargetPick') : null;

      if (targetType === 'You') {
        const targetName = ctx.pName(who);
        if (actContainer) {
          actContainer.innerHTML = `<div class="dice-result-banner">🎲 Outcome: ${dir}${val} pts for ${targetName}!</div>`;
        }
        ctx.setStatus(`Dice: ${dir}${val} points for ${targetName}`);
        setTimeout(() => {
          ClashMiniGames.applyDiceResult(who, signedPts, context);
        }, 2000);
      } else if (targetType === 'Opponent') {
        const targetOpp = opponents.slice().sort((a, b) => {
          const scA = (ctx.state.scores && ctx.state.scores[a]) || 0;
          const scB = (ctx.state.scores && ctx.state.scores[b]) || 0;
          return isPositive ? (scA - scB) : (scB - scA);
        })[0] || opponents[0] || 'cpu';

        const targetName = ctx.pName(targetOpp);
        if (actContainer) {
          actContainer.innerHTML = `<div class="dice-result-banner">🎲 Outcome: ${dir}${val} pts for Opponent (${targetName})!</div>`;
        }
        ctx.setStatus(`Dice: ${dir}${val} points for Opponent (${targetName})`);
        setTimeout(() => {
          ClashMiniGames.applyDiceResult(targetOpp, signedPts, context);
        }, 2000);
      } else {
        // 'You Pick'
        const bestTarget = isPositive ? who : (opponents[0] || who);
        if (isCpu) {
          const bestTargetName = ctx.pName(bestTarget);
          if (actContainer) {
            actContainer.innerHTML = `<div class="dice-result-banner">🎲 ${ctx.pName(who)} picked: ${bestTargetName} to move ${dir}${val} pts!</div>`;
          }
          ctx.setStatus(`${ctx.pName(who)} picked ${bestTargetName} to move ${dir}${val} points`);
          ctx.log(`🎲 YOU PICK: ${ctx.pName(who)} picked ${bestTargetName} to move ${dir}${val} points!`);
          setTimeout(() => {
            ClashMiniGames.applyDiceResult(bestTarget, signedPts, context);
          }, 2000);
        } else {
          if (actContainer) {
            actContainer.innerHTML = `<div class="dice-result-banner">🎲 You Pick! Pick what player has to move (${dir}${val} pts):</div>`;
          }
          ctx.setStatus(`You Pick! Pick what player has to move (${dir}${val} points)`);
          if (pickContainer) {
            pickContainer.style.display = 'flex';
            pickContainer.innerHTML = tracks.map(p => {
              const curScore = (ctx.state.scores && ctx.state.scores[p]) || 0;
              const newScore = Math.max(0, curScore + signedPts);
              const color = (ctx.win && typeof ctx.win.seatColor === 'function') ? ctx.win.seatColor(p) : '#f0c64a';
              return `
                <button type="button" class="target-player-btn" data-action="dice-pick-player" data-player="${p}" data-signed="${signedPts}" data-dir="${dir}" data-val="${val}">
                  <span style="color: ${color}; font-size: 1.05rem;">${ctx.pName(p)}</span>
                  <span class="target-score-shift">${curScore} pts → <strong>${newScore} pts</strong> (${dir}${val})</span>
                </button>
              `;
            }).join('');
            pickContainer.addEventListener('click', (e) => {
              const btn = e.target.closest('[data-action="dice-pick-player"]');
              if (btn) {
                const pSeat = btn.dataset.player;
                const sPts = parseInt(btn.dataset.signed, 10);
                const d = btn.dataset.dir;
                const v = parseInt(btn.dataset.val, 10);
                if (typeof onDicePickPlayer === 'function') {
                  onDicePickPlayer(pSeat, sPts, d, v);
                } else {
                  ClashMiniGames.onDicePickPlayer(pSeat, sPts, d, v, context);
                }
              }
            });
          }
        }
      }
    }, 2000);
  }

  /**
   * Handles interactive human player selection after a 'You Pick' die outcome.
   * @param {string} targetWho
   * @param {number} signedPts
   * @param {string} dir
   * @param {number} val
   * @param {Object} [context={}]
   */
  static onDicePickPlayer(targetWho, signedPts, dir, val, context = {}) {
    const ctx = ClashMiniGames._resolveCtx(context);
    const doc = ctx.doc;
    const actContainer = doc ? doc.getElementById('diceActions') : null;
    const pickContainer = doc ? doc.getElementById('diceTargetPick') : null;
    if (pickContainer) pickContainer.style.display = 'none';

    const targetName = ctx.pName(targetWho);
    const dirStr = dir || (signedPts >= 0 ? '+' : '−');
    const ptsVal = val || Math.abs(signedPts);
    if (actContainer) {
      actContainer.innerHTML = `<div class="dice-result-banner">🎲 You picked: ${targetName} to move ${dirStr}${ptsVal} pts!</div>`;
    }
    ctx.log(`🎲 YOU PICK: Player chose ${targetName} to move ${dirStr}${ptsVal} points!`);
    ctx.setStatus(`You picked ${targetName} to move ${dirStr}${ptsVal} points`);
    setTimeout(() => {
      ClashMiniGames.applyDiceResult(targetWho, signedPts, context);
    }, 2000);
  }

  /**
   * Closes the dice modal, adds the score delta, and resumes the match.
   * @param {string} targetWho
   * @param {number} signedPts
   * @param {Object} [context={}]
   */
  static applyDiceResult(targetWho, signedPts, context = {}) {
    const ctx = ClashMiniGames._resolveCtx(context);
    ctx.closeModal();

    const playerName = ctx.pName(targetWho);
    const signPrefix = signedPts >= 0 ? '+' : '';
    ctx.log(`🎲 DICE OUTCOME: ${playerName} ${signPrefix}${signedPts} pts!`);
    ctx.fancyCall(`DICE: ${signPrefix}${signedPts} PTS!`, 'signs', 2600);
    ctx.addScore(targetWho, signedPts, `Dice Roll: ${signPrefix}${signedPts}`, false, true);
    ClashMiniGames.resumeGameAfterSign(context);
  }

  /**
   * Opens the Spin-The-Wheel modal and draws the canvas.
   * @param {string} who
   * @param {Object} [context={}]
   */
  static openWheelSpinModal(who, context = {}) {
    const ctx = ClashMiniGames._resolveCtx(context);
    const isCpu = ctx.isSeatCpu(who);

    const modalHtml = `
      <div class="signs-modal-wrap">
        <h2 class="signs-modal-title">🎡 SPIN THE WHEEL</h2>
        <p class="signs-modal-subtitle">Spin for 1 of 8 exciting rewards or penalties:</p>
        <div class="wheel-arena">
          <div class="wheel-pointer"></div>
          <canvas id="wheelCanvas" class="wheel-canvas" width="240" height="240"></canvas>
          <div class="wheel-hub"></div>
        </div>
        <div id="wheelActions">
          <button type="button" class="dice-roll-btn" id="btnSpinWheel">SPIN WHEEL</button>
        </div>
      </div>
    `;
    ctx.showModal(modalHtml, false);

    const spinBtn = ctx.doc ? ctx.doc.getElementById('btnSpinWheel') : null;
    if (spinBtn) {
      spinBtn.addEventListener('click', () => {
        if (typeof performWheelSpin === 'function') performWheelSpin(who);
        else ClashMiniGames.performWheelSpin(who, context);
      });
    }

    const canvas = ctx.doc ? ctx.doc.getElementById('wheelCanvas') : null;
    if (canvas) {
      ClashMiniGames.drawWheelCanvas(canvas);
    }

    if (isCpu) {
      setTimeout(() => {
        ClashMiniGames.performWheelSpin(who, context);
      }, 700);
    }
  }

  /**
   * Draws 8 color-coded wheel segments with text labels on an HTML5 canvas.
   * @param {HTMLCanvasElement} canvas
   */
  static drawWheelCanvas(canvas) {
    if (!canvas || typeof canvas.getContext !== 'function') return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const cx = 120, cy = 120, r = 118;
    const colors = ['#10b981', '#ef4444', '#06b6d4', '#f97316', '#6366f1', '#a855f7', '#eab308', '#ec4899'];
    const n = CLASH_WHEEL_SLOTS.length;
    const step = (Math.PI * 2) / n;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (let i = 0; i < n; i++) {
      const angle = i * step;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, angle, angle + step);
      ctx.closePath();
      ctx.fillStyle = colors[i % colors.length];
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#ffffff';
      ctx.stroke();

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(angle + step / 2);
      ctx.textAlign = 'right';
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 11px Outfit, sans-serif';
      ctx.shadowColor = 'rgba(0,0,0,0.8)';
      ctx.shadowBlur = 4;
      ctx.fillText(CLASH_WHEEL_SLOTS[i], r - 12, 4);
      ctx.restore();
    }
  }

  /**
   * Animates the wheel spin rotation and executes the outcome.
   * @param {string} who
   * @param {Object} [context={}]
   */
  static performWheelSpin(who, context = {}) {
    const ctx = ClashMiniGames._resolveCtx(context);
    const doc = ctx.doc;

    const btn = doc ? doc.getElementById('btnSpinWheel') : null;
    if (btn) btn.disabled = true;

    const canvas = doc ? doc.getElementById('wheelCanvas') : null;
    if (!canvas) return;

    const slotIdx = Math.floor(Math.random() * CLASH_WHEEL_SLOTS.length);
    const n = CLASH_WHEEL_SLOTS.length;
    const slotAngleDeg = 360 / n;
    const targetAngle = 360 - (slotIdx * slotAngleDeg + slotAngleDeg / 2) - 90;
    const totalRot = 360 * 5 + targetAngle;

    canvas.style.transform = `rotate(${totalRot}deg)`;
    ctx.sfx.card();

    setTimeout(() => {
      const landedSlot = CLASH_WHEEL_SLOTS[slotIdx];
      const isHuman = ctx.isSeatHuman(who);
      const playerName = ctx.pName(who);

      const actEl = doc ? doc.getElementById('wheelActions') : null;
      if (actEl) {
        actEl.innerHTML = `<div class="dice-result-banner">🎡 Result: ${landedSlot.toUpperCase()}!</div>`;
      }

      if (!isHuman) {
        ctx.log(`🎡 WHEEL RESULT: ${playerName} spun and landed on "${landedSlot}"!`);
        ctx.fancyCall(`${playerName} WHEEL: ${landedSlot.toUpperCase()}!`, 'signs', 3200);
        ctx.setStatus(`${playerName} spun wheel and landed on ${landedSlot}`);
      } else {
        ctx.log(`🎡 WHEEL RESULT: Landed on "${landedSlot}"!`);
        ctx.fancyCall(`WHEEL: ${landedSlot.toUpperCase()}`, 'signs', 2800);
        ctx.setStatus(`Wheel landed on ${landedSlot}`);
      }

      setTimeout(() => {
        ctx.closeModal();
        setTimeout(() => {
          ClashMiniGames.executeSign(landedSlot, who, null, context);
          if (landedSlot !== 'Roll The Dice') {
            ClashMiniGames.resumeGameAfterSign(context);
          }
        }, 1000);
      }, 2400);
    }, 4000);
  }

  /**
   * Updates the SVG track signs billboard text for a block.
   * @param {number} blockId
   * @param {Object} [context={}]
   */
  static updateSignsBillboard(blockId, context = {}) {
    const ctx = ClashMiniGames._resolveCtx(context);
    const doc = ctx.doc;
    if (!doc) return;

    const tEl = doc.getElementById(`signsText${blockId}`);
    if (!tEl) return;

    const getSignsFn = context.getSignConfigs || (ctx.win && ctx.win.getSignConfigs);
    const blocks = (typeof getSignsFn === 'function') ? getSignsFn() : [];
    const block = blocks.find(b => b.id === blockId);
    if (!block) return;

    const remaining = (ctx.state && ctx.state.signsState && ctx.state.signsState[blockId])
      ? ctx.state.signsState[blockId]
      : block.initialSigns;

    tEl.textContent = `🪧 Signs: ${remaining.length}/${block.initialSigns.length}`;
  }

  /**
   * Displays info callout for remaining signs when user clicks a sign billboard.
   * @param {number} blockId
   * @param {Object} [context={}]
   */
  static onSignBillboardClick(blockId, context = {}) {
    const ctx = ClashMiniGames._resolveCtx(context);
    const getSignsFn = context.getSignConfigs || (ctx.win && ctx.win.getSignConfigs);
    const blocks = (typeof getSignsFn === 'function') ? getSignsFn() : [];
    const block = blocks.find(b => b.id === blockId);
    if (!block) return;

    const remaining = (ctx.state && ctx.state.signsState && ctx.state.signsState[blockId])
      ? ctx.state.signsState[blockId]
      : block.initialSigns;

    const defs = CLASH_SIGN_DEFS;
    const signsList = remaining.map(s => `• ${s} (${(defs[s] && defs[s].desc) || ''})`).join('\n');
    ctx.log(`🪧 SIGN BLOCK ${blockId} (Holes ${block.start}–${block.end}):\nRemaining Signs:\n${signsList}`);
    ctx.fancyCall(`BLOCK ${blockId}: ${remaining.length} SIGNS LEFT`, 'signs', 1600);
  }
}

